import { useCallback, useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { ENDPOINTS } from "@/api/application-api";
import { useAuth } from "@/context/auth-context";
import { handleResponse } from "@/utils/errorResponseHandlers";

export interface IdentityAuditEvent {
  id: number;
  organization_id: number;
  actor_user_id: number | null;
  /** Eagerly joined from users.email so the UI never has to do an N+1 lookup. */
  actor_email: string | null;
  target_user_id: number | null;
  target_email: string | null;
  workspace_id: number | null;
  event_type: string;
  source: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  payload: Record<string, any>;
  workos_event_id: string | null;
  request_id: string | null;
  connection_id: string | null;
  idp_request_id: string | null;
  created_at: string;
}

export interface AuditFilters {
  event_types?: string[];
  source?: string;
  actor_user_id?: number | null;
  target_user_id?: number | null;
  /** ISO-8601 lower bound (inclusive). */
  from?: string;
  /** ISO-8601 upper bound (exclusive). */
  to?: string;
  /** Free-text substring (event_type / source / payload). */
  q?: string;
  /** Page size. Backend caps at 200; default 50. */
  limit?: number;
}

export interface AuditPage {
  events: IdentityAuditEvent[];
  next_cursor: number | null;
  total_for_filter: number;
  limit: number;
}

export const AUDIT_PAGE_SIZE_OPTIONS = [25, 50, 100] as const;

/** Stable serializer for the filter object so two equivalent filter shapes
 *  produce the same react-query cache key. */
export const serializeFilters = (filters: AuditFilters): string =>
  JSON.stringify({
    event_types: [...(filters.event_types ?? [])].sort(),
    source: filters.source || "",
    actor_user_id: filters.actor_user_id ?? null,
    target_user_id: filters.target_user_id ?? null,
    from: filters.from || "",
    to: filters.to || "",
    q: filters.q || "",
    limit: filters.limit ?? 50,
  });

export const buildQueryString = (filters: AuditFilters, cursor?: number): string => {
  const params = new URLSearchParams();
  (filters.event_types ?? []).forEach(t => params.append("event_type", t));
  if (filters.source) {
    params.set("source", filters.source);
  }
  if (filters.actor_user_id !== null && filters.actor_user_id !== undefined) {
    params.set("actor_user_id", String(filters.actor_user_id));
  }
  if (filters.target_user_id !== null && filters.target_user_id !== undefined) {
    params.set("target_user_id", String(filters.target_user_id));
  }
  if (filters.from) {
    params.set("from", filters.from);
  }
  if (filters.to) {
    params.set("to", filters.to);
  }
  if (filters.q) {
    params.set("q", filters.q);
  }
  if (filters.limit !== null && filters.limit !== undefined) {
    params.set("limit", String(filters.limit));
  }
  if (cursor !== null && cursor !== undefined) {
    params.set("cursor", String(cursor));
  }
  return params.toString();
};

/**
 * Page-based audit log query. Uses the backend's id cursor, maintaining a
 * cursor stack as the user moves forward so prev/next pagination works.
 */
export const useIdentityAuditEventsPage = (
  organizationId: number | null,
  filters: AuditFilters = {},
) => {
  const userToken = useAuth()?.userToken;
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(AUDIT_PAGE_SIZE_OPTIONS[1]);
  const [pageStartCursors, setPageStartCursors] = useState<(number | undefined)[]>([undefined]);

  const queryFilters = useMemo(() => ({ ...filters, limit: pageSize }), [filters, pageSize]);
  const filterKey = serializeFilters(queryFilters);

  useEffect(() => {
    setPage(1);
    setPageStartCursors([undefined]);
  }, [filterKey]);

  const cursor = pageStartCursors[page - 1];
  const canFetchPage = page === 1 || cursor !== undefined;

  const query = useQuery<AuditPage>({
    queryKey: ["identity", "audit-events-page", organizationId, filterKey, page, cursor],
    enabled: organizationId !== null && Boolean(userToken) && canFetchPage,
    keepPreviousData: true,
    queryFn: async () => {
      const qs = buildQueryString(queryFilters, cursor);
      const base = ENDPOINTS.identity.audit_events(organizationId!);
      const url = qs ? `${base}?${qs}` : base;
      return fetch(url, {
        headers: { Authorization: `Bearer ${userToken ?? ""}` },
      }).then(handleResponse);
    },
  });

  const totalForFilter = query.data?.total_for_filter ?? 0;
  const totalPages = totalForFilter > 0 ? Math.ceil(totalForFilter / pageSize) : 0;
  const hasNext = page < totalPages;
  const hasPrev = page > 1;

  const setPageIndex = useCallback(
    (nextPage: number) => {
      if (nextPage < 1 || (totalPages > 0 && nextPage > totalPages)) {
        return;
      }

      if (nextPage > page && query.data?.next_cursor) {
        setPageStartCursors(prev => {
          if (prev.length > page) {
            return prev;
          }
          return [...prev, query.data!.next_cursor!];
        });
      }

      setPage(nextPage);
    },
    [page, query.data, totalPages],
  );

  const setPageSizeAndReset = useCallback((nextPageSize: number) => {
    setPageSize(nextPageSize);
  }, []);

  return {
    events: query.data?.events ?? [],
    totalForFilter,
    page,
    pageSize,
    totalPages,
    hasNext,
    hasPrev,
    setPageIndex,
    setPageSize: setPageSizeAndReset,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    refetch: query.refetch,
  };
};

/**
 * Single-page variant kept for older call sites that don't paginate
 * (e.g. tests, ad-hoc dashboards).
 */
export const useIdentityAuditEvents = (
  organizationId: number | null,
  filters: AuditFilters = {},
) => {
  const userToken = useAuth()?.userToken;
  const filterKey = serializeFilters(filters);
  return useQuery<AuditPage>({
    queryKey: ["identity", "audit-events-single", organizationId, filterKey],
    enabled: organizationId !== null && Boolean(userToken),
    queryFn: async () => {
      const qs = buildQueryString(filters);
      const base = ENDPOINTS.identity.audit_events(organizationId!);
      const url = qs ? `${base}?${qs}` : base;
      return fetch(url, {
        headers: { Authorization: `Bearer ${userToken ?? ""}` },
      }).then(handleResponse);
    },
  });
};

/**
 * Triggers a download of the filtered audit log as CSV. The endpoint
 * requires the bearer token, so we can't just `window.open` it — we
 * fetch it, build a blob URL, and click an anchor.
 */
export const downloadAuditCsv = async (
  organizationId: number,
  filters: AuditFilters,
  userToken: string,
): Promise<void> => {
  const qs = buildQueryString(filters);
  const base = ENDPOINTS.identity.audit_events_csv(organizationId);
  const url = qs ? `${base}?${qs}` : base;
  const response = await fetch(url, {
    headers: { Authorization: `Bearer ${userToken}` },
  });
  if (!response.ok) {
    throw new Error(`CSV export failed (${response.status})`);
  }
  const blob = await response.blob();
  const blobUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = blobUrl;
  a.download = `identity-audit-org-${organizationId}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(blobUrl);
};
